import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { GroupsService } from 'src/app/views/groups/groups.service';
import { ModelsService } from '../../../views/my-space/my-models/models.service';
import { ToastService } from '../../services/toast/toast.service';

interface AiModel {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-models-dialog',
  templateUrl: './models-dialog.component.html',
  styleUrls: ['./models-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressBarModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    QuillModule,
    MatDialogModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
  ],
})
export class ModelsDialogComponent implements OnInit, OnDestroy {
  @ViewChild('editor') editor: QuillEditorComponent;

  private fb = inject(UntypedFormBuilder);
  private toast = inject(ToastService);
  private modelService = inject(ModelsService);
  private translate = inject(TranslateService);
  private groupService = inject(GroupsService);
  private authService = inject(AuthService);
  public dialogRef = inject(MatDialogRef<ModelsDialogComponent>);
  public dialogData: any = inject(MAT_DIALOG_DATA);

  template;
  form;
  loading = false;
  data = '';

  canUseAI = this.authService.canUseAI();
  canUndo = false;
  canRedo = false;
  currentHeaderLabel = 'Quill.Header.Normal';
  private autoSaveSub?: Subscription;

  // AI Generation state
  aiSectionExpanded = signal(true);
  aiGenerating = signal(false);
  aiExampleText = signal('');
  aiInstructions = signal('');
  attachedFiles = signal<{ id: string; file: File; url?: string; name: string; uploading?: boolean; filename?: string }[]>([]);

  headerOptions = [
    { id: 1, text: 'Quill.Header.1', icon: 'format_h1' },
    { id: 2, text: 'Quill.Header.2', icon: 'format_h2' },
    { id: 3, text: 'Quill.Header.3', icon: 'format_h3' },
    { id: 4, text: 'Quill.Header.4', icon: 'format_h4' },
    { id: 5, text: 'Quill.Header.5', icon: 'format_h5' },
    { id: 6, text: 'Quill.Header.6', icon: 'format_h6' },
    { id: false, text: 'Quill.Header.Normal', icon: 'text_fields' },
  ];

  AiModels: AiModel[] = [
    { value: 'gemini-3-flash-preview', viewValue: 'Flash' },
    { value: 'gemini-3.1-pro-preview', viewValue: 'Pro' },
  ];
  groups = [{ name: 'Global.None', _id: 'private' }];
  quillModules = {
    toolbar: '#models-toolbar',
    keyboard: {
      bindings: {
        tab: {
          key: 9,
          handler: () => true, // Default handling
        },
      },
    },
  };

  ngOnInit(): void {
    if (this.dialogData.type === 'create') {
      this.translate.get('Global.NewTemplate').subscribe((translatedName) => {
        const defaultName = translatedName !== 'Global.NewTemplate' ? translatedName : 'New Template';

        this.modelService.createTemplate({ name: defaultName }).subscribe({
          next: (res) => {
            if (res && res._id) {
              this.dialogData._id = res._id;
              this.dialogData.type = 'edit';

              // If it's a resumed draft, fetch its data
              this.modelService.getTemplate(res._id).subscribe((draft) => {
                if (draft) {
                  // If the draft still has the translation key as name (from a previous failed run), translate it
                  const finalName = draft.name === 'Global.NewTemplate' ? defaultName : draft.name || '';

                  this.form.patchValue({
                    name: finalName,
                    data: draft.data || '',
                    aiModel: draft.aiModel || 'gemini-3-flash-preview',
                    group: draft.group,
                  });

                  if (draft.mediaContent && draft.mediaContent.length > 0) {
                    const files = draft.mediaContent.map((m: any, i: number) => ({
                      id: 'restored_' + i,
                      url: m.url,
                      filename: m.filename,
                      name: m.filename ? m.filename.split('/').pop() : 'Attached File',
                      uploading: false,
                    }));
                    this.attachedFiles.set(files);
                  }
                }
              });
            }
          },
          error: (err) => console.error('Failed to create template draft', err),
        });
      });
    } else if (this.dialogData.mediaContent && this.dialogData.mediaContent.length > 0) {
      // For normally edited templates that might have mediaContent
      const files = this.dialogData.mediaContent.map((m: any, i: number) => ({
        id: 'restored_' + i,
        url: m.url,
        filename: m.filename,
        name: m.filename ? m.filename.split('/').pop() : 'Attached File',
        uploading: false,
      }));
      this.attachedFiles.set(files);
    }

    if (this.dialogData.fineTunedModel) {
      this.AiModels.push({
        value: this.dialogData.fineTunedModel,
        viewValue: this.dialogData.fineTunedModel,
      });
    }
    this.getGroups();
    this.form = this.fb.group({
      name: [this.dialogData.type == 'edit' ? this.dialogData.name : '', [Validators.required, Validators.maxLength(100)]],
      data: [this.dialogData.type == 'edit' ? this.dialogData.data : '', [Validators.required, this.noWhitespaceValidator]],
      aiModel: [this.dialogData.aiModel || 'gemini-3-flash-preview', [Validators.required]],
      group: [this.dialogData.group || true],
      keepFormatting: [this.dialogData.keepFormatting || true],
    });

    this.autoSaveSub = this.form.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
      this.autoSave();
    });
  }

  public noWhitespaceValidator(control: FormControl) {
    return (control.value || '').trim().length ? null : { whitespace: true };
  }

  getGroups() {
    this.groupService.getGroups().subscribe((groups) => {
      this.groups = [{ name: 'Global.None', _id: 'private' }];
      groups.forEach((group) => {
        this.groups.push(group);
      });
    });
  }

  dataIsEmpty() {
    const result = this.form.value?.data?.replace(/<[^>]*>?/gm, '').trim().length == 0;
    return result;
  }

  toggleAiSection() {
    this.aiSectionExpanded.update((v) => !v);
  }

  autoSave() {
    if (!this.dialogData._id) return;

    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    const mediaContent = readyAttachments.map((a) => ({ url: a.url!, filename: a.filename }));

    const formValue = this.form.getRawValue();

    this.modelService
      .editTemplate({
        _id: this.dialogData._id,
        name: formValue.name || this.dialogData.name || this.translate.instant('Global.NewTemplate') || 'New Template',
        data: formValue.data || '',
        aiModel: formValue.aiModel || 'gemini-3-flash-preview',
        group: formValue.group,
        keepFormatting: formValue.keepFormatting,
        mediaContent: mediaContent,
      })
      .subscribe({
        error: (err) => console.error('Failed to auto-save template draft', err),
      });
  }

  async onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = Date.now().toString() + '_' + i;
      this.attachedFiles.update((arr) => [...arr, { id: fileId, file, name: file.name, uploading: true }]);

      try {
        const folderId = this.dialogData._id || 'temp_' + Date.now();
        const filename = `template_examples/${folderId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
        const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
        const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

        this.attachedFiles.update((arr) => arr.map((f) => (f.id === fileId ? { ...f, url: gsUrl, filename: filename, uploading: false } : f)));
        this.autoSave();
      } catch (err) {
        console.error('File upload failed', err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
        this.attachedFiles.update((arr) => arr.filter((f) => f.id !== fileId));
      }
    }
    event.target.value = '';
  }

  removeAttachment(fileId: string) {
    const fileObj = this.attachedFiles().find((f) => f.id === fileId);
    if (fileObj && fileObj.filename) {
      this.authService.deleteFile(fileObj.filename).catch((e) => console.error('Cleanup failed', e));
    }
    this.attachedFiles.update((arr) => arr.filter((f) => f.id !== fileId));
    this.autoSave();
  }

  ngOnDestroy() {
    if (this.autoSaveSub) {
      this.autoSaveSub.unsubscribe();
    }
    // Trigger a final auto-save when closing the draft, assuming we aren't currently submitting the final 'active' form
    if (!this.loading) {
      this.autoSave();
    }
    // Files are now persisted in the draft, so we don't delete them on close.
  }

  generateWithAI() {
    const exampleText = this.aiExampleText();
    const instructions = this.aiInstructions();
    const files = this.attachedFiles()
      .filter((f) => !f.uploading && f.url)
      .map((f) => f.url);

    let currentTemplate = this.form.value.data;
    if (currentTemplate) {
      // Strip out empty paragraphs and whitespace to check if there's actual content
      const stripped = currentTemplate.replace(/<[^>]*>/g, '').trim();
      if (!stripped) {
        currentTemplate = undefined; // Treat as empty
      }
    }

    if (!exampleText && !instructions && files.length === 0) return;

    this.aiGenerating.set(true);
    this.modelService
      .generateTemplateWithAI({
        exampleText,
        instructions,
        files,
        language: this.translate.currentLang,
        currentTemplate,
      })
      .subscribe({
        next: (res) => {
          this.aiGenerating.set(false);
          if (res.template) {
            this.form.controls['data'].setValue(res.template);
            this.aiSectionExpanded.set(false);
            this.toast.snackbar.open(this.translate.instant('Templates.AI.Success'), 'Ok', { duration: 3000 });
          }
        },
        error: (err) => {
          console.error(err);
          this.aiGenerating.set(false);
          this.toast.snackbar.open(this.translate.instant('Templates.AI.Error'), 'Ok', { duration: 3000 });
        },
      });
  }

  submit() {
    if (this.form.invalid || this.dataIsEmpty()) {
      return;
    }
    this.loading = true;
    const { name, data, aiModel, group, keepFormatting } = this.form.value;

    // We always use editTemplate now because even creates are drafted as edits.
    // Ensure mediaContent is preserved when submitting the final form.
    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    const mediaContent = readyAttachments.map((a) => ({ url: a.url!, filename: a.filename }));

    this.modelService
      .editTemplate({
        _id: this.dialogData._id,
        name,
        data,
        aiModel,
        group,
        keepFormatting,
        mediaContent,
        status: 'active',
      })
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Close', { duration: 3000 });
          this.dialogRef.close(true);
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 3000 });
        },
      });
  }

  updateHistoryState() {
    if (this.editor?.quillEditor?.history) {
      const stack = this.editor.quillEditor.history.stack;
      this.canUndo = stack.undo.length > 0;
      this.canRedo = stack.redo.length > 0;
    }
  }

  undo() {
    this.editor?.quillEditor?.history?.undo();
    this.updateHistoryState();
  }

  redo() {
    this.editor?.quillEditor?.history?.redo();
    this.updateHistoryState();
  }

  applyHeader(level: number | boolean) {
    this.editor.quillEditor.format('header', level, 'user');
    this.updateHeaderLabel(level);
  }

  clearFormatting() {
    const range = this.editor.quillEditor.getSelection();
    if (range) {
      this.editor.quillEditor.removeFormat(range.index, range.length, 'user');
      this.updateHeaderLabel(false); // Reset to Normal
    }
  }

  onContentChanged(event: any) {
    if (event.source === 'user') {
      this.updateHistoryState();
    }
  }

  onSelectionChanged(event: any) {
    if (event.range) {
      const format = this.editor.quillEditor.getFormat(event.range.index, event.range.length);
      const headerLevel = format.header || false;
      this.updateHeaderLabel(headerLevel);
      this.updateHistoryState();
    }
  }

  private updateHeaderLabel(level: any) {
    const found = this.headerOptions.find((opt) => opt.id === level);
    if (found) {
      this.currentHeaderLabel = found.text;
    } else {
      this.currentHeaderLabel = 'Quill.Header.Normal';
    }
  }
}
